import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowUpRight, Shield, X } from "lucide-react";
import api from "../../lib/api";

/**
 * ApplyTemplateDialog — Phase 5 propagation preview + confirm.
 *
 * The conversational impact summary is the emotional core of Phase 5:
 *   * "14 organizations will inherit this baseline."
 *   * "3 stricter local locks will be respected."
 *   * "2 active emergency overrides will remain untouched."
 * No raw diffs, no JSON dumps, no engineering jargon.
 *
 * Flow: simulate → display impact → operator clicks confirm → apply.
 * The dialog itself never proceeds without an explicit confirm tap;
 * the backend also enforces `confirm:true`.
 */
export const ApplyTemplateDialog = ({ template, targetOrg, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [scope, setScope] = useState("subtree");
    const [impact, setImpact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await api.post(`/operational-base/templates/${template.id}/simulate`, {
                    target_org_id: targetOrg.id,
                    scope,
                });
                if (!cancelled) setImpact(res.data);
            } catch (err) {
                if (!cancelled) {
                    toast.error(err?.response?.data?.detail || t("ob.templates.simulate_error"));
                    setImpact(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [template.id, targetOrg.id, scope, t]);

    const handleApply = async () => {
        setApplying(true);
        try {
            const res = await api.post(`/operational-base/templates/${template.id}/apply`, {
                target_org_id: targetOrg.id,
                scope,
                confirm: true,
            });
            toast.success(t("ob.templates.apply_success", {
                name: template.name,
                target: targetOrg.name,
            }));
            onSuccess && onSuccess(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.templates.apply_error"));
        } finally {
            setApplying(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="ob-template-apply-dialog"
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.6)" }}
            onClick={onClose}
        >
            <div
                role="document"
                className="ob-card w-full max-w-xl max-h-[90vh] overflow-y-auto"
                style={{ background: "var(--ob-surface-elevated)", padding: "2rem" }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 mb-5">
                    <div className="flex items-start gap-3">
                        <div className="ob-tile-icon flex-shrink-0" aria-hidden="true">
                            <ArrowUpRight size={20} strokeWidth={1.5} />
                        </div>
                        <div>
                            <h2 className="text-xl" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }}>
                                {t("ob.templates.apply_dialog_title", { name: template.name })}
                            </h2>
                            <p className="text-sm mt-1" style={{ color: "var(--ob-text-secondary)" }}>
                                {t("ob.templates.apply_dialog_subtitle", { target: targetOrg.name })}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("common.close")}
                        className="p-1 rounded hover:bg-white/5"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid="ob-template-apply-close"
                    >
                        <X size={18} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </header>

                {/* Scope selector */}
                <div className="mb-5">
                    <p className="ob-eyebrow mb-2">{t("ob.templates.scope_legend")}</p>
                    <div className="grid grid-cols-2 gap-2">
                        {["subtree", "self_only"].map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => setScope(opt)}
                                data-testid={`ob-template-scope-${opt}`}
                                className="rounded-xl px-3 py-2.5 text-sm transition"
                                style={{
                                    background: scope === opt ? "rgba(16,185,129,0.12)" : "rgba(255,255,255,0.03)",
                                    border: scope === opt ? "1px solid rgba(16,185,129,0.30)" : "1px solid rgba(255,255,255,0.06)",
                                    color: scope === opt ? "#A7F3D0" : "var(--ob-text-secondary)",
                                }}
                            >
                                {t(`ob.templates.scope_${opt}`)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Conversational impact summary */}
                <div
                    className="rounded-xl p-4 space-y-2 text-sm mb-5"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                    data-testid="ob-template-impact-block"
                >
                    {loading ? (
                        <p style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.activity.loading")}</p>
                    ) : !impact ? (
                        <p style={{ color: "var(--ob-text-tertiary)" }}>{t("ob.templates.simulate_error")}</p>
                    ) : (
                        <>
                            <p style={{ color: "var(--ob-text-secondary)" }}>
                                {t("ob.templates.impact_intro", { template: template.name, target: targetOrg.name })}
                            </p>
                            <ul className="space-y-1 pt-1">
                                {(impact.plain_language_lines || []).map((line, i) => (
                                    <li key={i} style={{ color: line.toLowerCase().includes("emergency") ? "#93C5FD"
                                        : line.toLowerCase().includes("respect") ? "#A7F3D0"
                                        : "var(--ob-text-secondary)" }}>
                                        · {line}
                                    </li>
                                ))}
                            </ul>
                            {impact.preserved && impact.preserved.length > 0 && (
                                <div className="pt-3 mt-2 space-y-1.5" style={{ borderTop: "1px dashed rgba(255,255,255,0.08)" }}>
                                    <p className="ob-eyebrow inline-flex items-center gap-1.5">
                                        <Shield size={11} strokeWidth={1.5} style={{ color: "#A7F3D0" }} aria-hidden="true" />
                                        {t("ob.templates.preserved_label")}
                                    </p>
                                    {impact.preserved.slice(0, 6).map((p, i) => (
                                        <p key={i} className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                                            {t(`ob.templates.preserved_${p.reason_class}`, {
                                                org: p.org_name,
                                                module: p.note || p.module_name,
                                            })}
                                        </p>
                                    ))}
                                    {impact.preserved.length > 6 && (
                                        <p className="text-xs" style={{ color: "var(--ob-text-tertiary)" }}>
                                            {t("ob.templates.preserved_more", { count: impact.preserved.length - 6 })}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <p className="text-xs mb-5" style={{ color: "var(--ob-text-tertiary)" }}>
                    {t("ob.templates.rollback_reassurance")}
                </p>

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={applying}
                        data-testid="ob-template-cancel-btn"
                        className="text-sm px-4 py-2 rounded-full"
                        style={{ color: "var(--ob-text-secondary)" }}
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={loading || !impact || applying}
                        data-testid="ob-template-confirm-apply-btn"
                        className="inline-flex items-center gap-1.5 text-sm rounded-full px-5 py-2.5 transition"
                        style={{
                            background: "#10B981",
                            color: "#0B0F12",
                            opacity: (loading || !impact || applying) ? 0.5 : 1,
                            cursor: (loading || !impact || applying) ? "not-allowed" : "pointer",
                        }}
                    >
                        <ArrowUpRight size={14} strokeWidth={1.5} aria-hidden="true" />
                        {applying ? t("ob.templates.applying") : t("ob.templates.confirm_apply")}
                    </button>
                </div>
            </div>
        </div>
    );
};
